import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2,
  DoorOpen,
  Search,
  Building2,
  Layers,
  AlertTriangle,
  ChevronRight,
  Check
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { roomsAPI, categoriesAPI, stationsAPI } from '../../services/api';

const RoomsManagement = () => {
  const [rooms, setRooms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStation, setFilterStation] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [deletingRoom, setDeletingRoom] = useState(null);
  
  // Step-based form state
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    roomNumber: '',
    capacity: '2',
    floor: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [filterStation, filterStatus]);

  const fetchData = async () => {
    try {
      const [stationsRes, categoriesRes] = await Promise.all([
        stationsAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      setStations(stationsRes.data.stations);
      setCategories(categoriesRes.data.categories);
      await fetchRooms();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    }
  };

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStation) params.station = filterStation;
      if (filterStatus) params.status = filterStatus;
      
      const response = await roomsAPI.getAll(params);
      setRooms(response.data.rooms);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (room = null) => {
    if (room) {
      setEditingRoom(room);
      setSelectedStation(stations.find(s => s._id === room.station?._id) || null);
      setSelectedCategory(categories.find(c => c._id === room.category?._id) || null);
      setFormData({
        roomNumber: room.roomNumber,
        capacity: room.capacity.toString(),
        floor: room.floor || ''
      });
      setCurrentStep(3); // Go directly to room details when editing
    } else {
      setEditingRoom(null);
      setSelectedStation(null);
      setSelectedCategory(null);
      setFormData({
        roomNumber: '',
        capacity: '2',
        floor: ''
      });
      setCurrentStep(1); // Start from station selection when adding new
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRoom(null);
    setSelectedStation(null);
    setSelectedCategory(null);
    setCurrentStep(1);
  };

  const handleOpenDeleteModal = (room) => {
    setDeletingRoom(room);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingRoom(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectStation = (station) => {
    setSelectedStation(station);
    setSelectedCategory(null);
    setCurrentStep(2);
  };

  const handleSelectCategory = (category) => {
    setSelectedCategory(category);
    // Auto-generate room number suggestion
    const stationCode = selectedStation?.code || '';
    const categoryPrefix = category.name.includes('AC Deluxe') ? 'AD'
      : category.name.includes('AC Standard') ? 'AS'
      : category.name.includes('Non-AC Deluxe') ? 'ND'
      : category.name.includes('Non-AC Standard') ? 'NS'
      : category.name.includes('AC Dormitory') ? 'ACD'
      : 'NCD';
    
    // Count existing rooms in this category
    const existingRoomsCount = rooms.filter(r => 
      r.category?._id === category._id && r.station?._id === selectedStation?._id
    ).length;
    
    const suggestedNumber = `${stationCode}-${categoryPrefix}-${String(existingRoomsCount + 1).padStart(3, '0')}`;
    
    setFormData(prev => ({
      ...prev,
      roomNumber: suggestedNumber,
      capacity: category.name.includes('Dormitory') ? '1' : '2'
    }));
    setCurrentStep(3);
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setSelectedStation(null);
      setCurrentStep(1);
    } else if (currentStep === 3 && !editingRoom) {
      setSelectedCategory(null);
      setCurrentStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      roomNumber: formData.roomNumber,
      category: selectedCategory?._id,
      capacity: parseInt(formData.capacity),
      floor: formData.floor
    };
    
    try {
      if (editingRoom) {
        await roomsAPI.update(editingRoom._id, data);
        toast.success('Room updated successfully');
      } else {
        await roomsAPI.create(data);
        toast.success('Room created successfully');
      }
      
      handleCloseModal();
      fetchRooms();
    } catch (error) {
      console.error('Error saving room:', error);
      toast.error(error.response?.data?.error || 'Failed to save room');
    }
  };

  const handleDelete = async () => {
    if (!deletingRoom) return;

    try {
      await roomsAPI.delete(deletingRoom._id);
      toast.success('Room deleted successfully');
      handleCloseDeleteModal();
      fetchRooms();
    } catch (error) {
      console.error('Error deleting room:', error);
      toast.error(error.response?.data?.error || 'Failed to delete room');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      available: 'badge-success',
      occupied: 'badge-danger',
      maintenance: 'badge-warning',
      reserved: 'badge-info'
    };
    return styles[status] || 'badge-neutral';
  };

  const filteredRooms = rooms.filter((room) => {
    return room.roomNumber.toLowerCase().includes(search.toLowerCase()) ||
           room.station?.name?.toLowerCase().includes(search.toLowerCase()) ||
           room.category?.name?.toLowerCase().includes(search.toLowerCase());
  });

  // Filter categories by selected station
  const stationCategories = selectedStation 
    ? categories.filter(c => c.station?._id === selectedStation._id)
    : [];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-midnight-900">
              Rooms Management
            </h1>
            <p className="text-midnight-600 mt-1">
              Manage rooms across all stations
            </p>
          </div>
          <button 
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Room
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-midnight-400" />
            <input
              type="text"
              placeholder="Search rooms..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-12"
            />
          </div>
          <select
            value={filterStation}
            onChange={(e) => setFilterStation(e.target.value)}
            className="input-field max-w-xs"
          >
            <option value="">All Stations</option>
            {stations.map((station) => (
              <option key={station._id} value={station._id}>
                {station.name} ({station.code})
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input-field max-w-xs"
          >
            <option value="">All Status</option>
            <option value="available">Available</option>
            <option value="occupied">Occupied</option>
            <option value="maintenance">Maintenance</option>
            <option value="reserved">Reserved</option>
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-green-600 text-sm font-medium">Available</p>
            <p className="text-2xl font-bold text-green-700">
              {rooms.filter(r => r.status === 'available').length}
            </p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-600 text-sm font-medium">Occupied</p>
            <p className="text-2xl font-bold text-red-700">
              {rooms.filter(r => r.status === 'occupied').length}
            </p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="text-yellow-600 text-sm font-medium">Maintenance</p>
            <p className="text-2xl font-bold text-yellow-700">
              {rooms.filter(r => r.status === 'maintenance').length}
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-blue-600 text-sm font-medium">Reserved</p>
            <p className="text-2xl font-bold text-blue-700">
              {rooms.filter(r => r.status === 'reserved').length}
            </p>
          </div>
        </div>

        {/* Rooms Table */}
        <div className="card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredRooms.length === 0 ? (
            <div className="text-center py-20">
              <DoorOpen className="w-16 h-16 text-midnight-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-midnight-700">No rooms found</h3>
              <p className="text-midnight-500 mt-2">Add a new room to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-midnight-50 border-b border-midnight-100">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Room</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Station</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Category</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Floor</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Capacity</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Status</th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-midnight-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-midnight-100">
                  {filteredRooms.map((room) => (
                    <tr key={room._id} className="hover:bg-midnight-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-railway-100 rounded-lg flex items-center justify-center">
                            <DoorOpen className="w-5 h-5 text-railway-600" />
                          </div>
                          <span className="font-semibold text-midnight-900">{room.roomNumber}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-midnight-600">
                          <Building2 className="w-4 h-4" />
                          <span>{room.station?.code}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-midnight-600">
                          <Layers className="w-4 h-4" />
                          <span>{room.category?.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-midnight-600">
                        {room.floor || '—'}
                      </td>
                      <td className="px-6 py-4 text-midnight-600">
                        {room.capacity} persons
                      </td>
                      <td className="px-6 py-4">
                        <span className={getStatusBadge(room.status)}>
                          {room.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenModal(room)}
                            className="p-2 text-midnight-500 hover:text-railway-500 hover:bg-railway-50 rounded-lg transition-colors"
                            title="Edit Room"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenDeleteModal(room)}
                            className="p-2 text-midnight-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Room"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal with Steps */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingRoom ? 'Edit Room' : 'Add New Room'}
        size="lg"
      >
        <div className="space-y-6">
          {/* Step Indicator */}
          {!editingRoom && (
            <div className="flex items-center justify-center gap-2 mb-6">
              {[
                { num: 1, label: 'Station' },
                { num: 2, label: 'Category' },
                { num: 3, label: 'Details' }
              ].map((step, index) => (
                <React.Fragment key={step.num}>
                  <div className="flex items-center gap-2">
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                      ${currentStep >= step.num 
                        ? 'bg-railway-500 text-white' 
                        : 'bg-midnight-100 text-midnight-400'
                      }
                    `}>
                      {currentStep > step.num ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        step.num
                      )}
                    </div>
                    <span className={`text-sm font-medium hidden sm:inline ${
                      currentStep >= step.num ? 'text-midnight-900' : 'text-midnight-400'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {index < 2 && (
                    <ChevronRight className={`w-4 h-4 ${
                      currentStep > step.num ? 'text-railway-500' : 'text-midnight-300'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Step 1: Select Station */}
          {currentStep === 1 && !editingRoom && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-midnight-900">Select Station</h3>
              <p className="text-midnight-600 text-sm">Choose the station where you want to add a room</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-80 overflow-y-auto">
                {stations.map((station) => (
                  <button
                    key={station._id}
                    onClick={() => handleSelectStation(station)}
                    className={`
                      p-4 rounded-xl border-2 text-left transition-all
                      ${selectedStation?._id === station._id
                        ? 'border-railway-500 bg-railway-50'
                        : 'border-midnight-200 hover:border-railway-300 hover:bg-midnight-50'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-railway-100 rounded-lg flex items-center justify-center">
                        <span className="font-bold text-railway-700">{station.code}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-midnight-900">{station.name}</p>
                        <p className="text-sm text-midnight-500">{station.city}, {station.state}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Select Category */}
          {currentStep === 2 && !editingRoom && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-midnight-900">Select Category</h3>
                  <p className="text-midnight-600 text-sm">Choose the room category at {selectedStation?.name}</p>
                </div>
                <button onClick={handleBack} className="text-railway-600 hover:text-railway-700 text-sm font-medium">
                  ← Back to Stations
                </button>
              </div>
              
              {stationCategories.length === 0 ? (
                <div className="text-center py-8">
                  <Layers className="w-12 h-12 text-midnight-300 mx-auto mb-3" />
                  <p className="text-midnight-600">No categories found for this station.</p>
                  <p className="text-midnight-500 text-sm">Please create categories first.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-80 overflow-y-auto">
                  {stationCategories.map((category) => (
                    <button
                      key={category._id}
                      onClick={() => handleSelectCategory(category)}
                      className={`
                        p-4 rounded-xl border-2 text-left transition-all
                        ${selectedCategory?._id === category._id
                          ? 'border-railway-500 bg-railway-50'
                          : 'border-midnight-200 hover:border-railway-300 hover:bg-midnight-50'
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-midnight-100 rounded-lg flex items-center justify-center">
                          <Layers className="w-5 h-5 text-midnight-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-midnight-900">{category.name}</p>
                          <p className="text-sm text-midnight-500">
                            {rooms.filter(r => r.category?._id === category._id && r.station?._id === selectedStation?._id).length} rooms
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Room Details */}
          {currentStep === 3 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingRoom && (
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-midnight-900">Room Details</h3>
                    <p className="text-midnight-600 text-sm">
                      Adding to {selectedCategory?.name} at {selectedStation?.code}
                    </p>
                  </div>
                  <button type="button" onClick={handleBack} className="text-railway-600 hover:text-railway-700 text-sm font-medium">
                    ← Back to Categories
                  </button>
                </div>
              )}

              {editingRoom && (
                <div className="p-3 bg-midnight-50 rounded-lg mb-4">
                  <p className="text-sm text-midnight-600">
                    <strong>Station:</strong> {selectedStation?.name} ({selectedStation?.code})
                  </p>
                  <p className="text-sm text-midnight-600">
                    <strong>Category:</strong> {selectedCategory?.name}
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-midnight-700 mb-2">
                  Room Number *
                </label>
                <input
                  type="text"
                  name="roomNumber"
                  value={formData.roomNumber}
                  onChange={handleChange}
                  placeholder="e.g., NDLS-AD-001"
                  className="input-field"
                  required
                />
                <p className="text-xs text-midnight-500 mt-1">
                  Format suggestion: STATION-CATEGORY-NUMBER (e.g., NDLS-AD-001)
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-midnight-700 mb-2">
                    Capacity
                  </label>
                  <input
                    type="number"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    placeholder="e.g., 2"
                    className="input-field"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-midnight-700 mb-2">
                    Floor
                  </label>
                  <input
                    type="text"
                    name="floor"
                    value={formData.floor}
                    onChange={handleChange}
                    placeholder="e.g., Floor 1"
                    className="input-field"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={handleCloseModal} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">
                  {editingRoom ? 'Update Room' : 'Create Room'}
                </button>
              </div>
            </form>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        title="Delete Room"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-4 p-4 bg-red-50 rounded-xl">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h4 className="font-semibold text-red-900">Are you sure?</h4>
              <p className="text-sm text-red-700 mt-1">
                This will deactivate room <strong>"{deletingRoom?.roomNumber}"</strong>. 
                {deletingRoom?.status === 'occupied' && (
                  <span className="block mt-2 font-semibold">
                    ⚠️ This room is currently occupied. Please checkout the guest first.
                  </span>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex gap-3 pt-2">
            <button onClick={handleCloseDeleteModal} className="btn-secondary flex-1">
              Cancel
            </button>
            <button 
              onClick={handleDelete} 
              disabled={deletingRoom?.status === 'occupied'}
              className={`flex-1 font-semibold py-3 px-6 rounded-xl transition-colors ${
                deletingRoom?.status === 'occupied'
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              Delete Room
            </button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default RoomsManagement;
